import React from "react";
import * as axios from "axios";
import {
  Button, Switch, FormControlLabel, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreFromTrashIcon from "@material-ui/icons/RestoreFromTrash";
import BuildIcon from "@material-ui/icons/Build";
import HistoryIcon from "@material-ui/icons/History";
import GetAppIcon from "@material-ui/icons/GetApp";
import LocalGasStationIcon from "@material-ui/icons/LocalGasStation";
import CarModal from "./CarModal";
import CarServiceModal from "./CarServiceModal";
import {formatDate} from "./shiftUtils";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

// A car's real km within this many km of its latest service's
// next_service_km gets flagged yellow ("due soon") instead of red ("due
// now", i.e. real km already at or past the threshold).
const SERVICE_DUE_SOON_KM = 500;

// ITP/RCA/rovinietă expiry within this many days gets flagged yellow
// ("expires soon") instead of red (already expired).
const DOC_WARNING_DAYS = 7;

const DOC_FIELDS = [
  {key: 'itp_expires_at', label: 'ITP'},
  {key: 'rca_expires_at', label: 'RCA'},
  {key: 'rovinieta_expires_at', label: 'Rovinietă'},
];

// Parses a "YYYY-MM-DD..." date string (what the backend sends) into a
// local midnight Date — not `new Date(str)`, which parses as UTC midnight
// and can land on the wrong calendar day once compared to a local "today".
function parseDateOnly(value) {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

// 'expired' (red) once the date has passed, 'warning' (yellow) once it's
// within DOC_WARNING_DAYS, 'ok' (neutral) for any other set date, null only
// when the date was never set at all — a document that's simply fine still
// needs to show *something*, otherwise a car with every date on file looks
// identical to one nobody ever filled in.
function formatNumber(value, digits) {
  return Number(value).toLocaleString('ro-RO', {minimumFractionDigits: digits, maximumFractionDigits: digits});
}

function docStatus(expiresAt) {
  if (!expiresAt) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((parseDateOnly(expiresAt) - today) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) {
    return 'expired';
  }
  if (diffDays <= DOC_WARNING_DAYS) {
    return 'warning';
  }
  return 'ok';
}

export default class Cars extends React.Component {
  state = {
    cars: [],
    kmByCarId: {},
    fuelByCarId: {},
    fuelWindow: null,
    fuelHistoryAnchor: null,
    fuelHistoryCar: null,
    fuelHistoryEntries: [],
    latestServiceByCarId: {},
    modalOpen: false,
    editingCar: null,
    showDeleted: false,
    serviceModalOpen: false,
    serviceModalCar: null,
    kmHistoryAnchor: null,
    kmHistoryCar: null,
    kmHistoryEntries: [],
    kmEditCar: null,
    kmEditValue: '',
    kmEditSaving: false,
    kmEditError: '',
  };

  componentDidMount() {
    this.getCars();
    this.getKm();
    this.getFuel();
    this.getLatestServices();
  }

  // Each car's Rompetrol fuel over the last 30 full days, next to its real km
  // over the same days (the backend's ListCarFuel; fuel is imported daily
  // from Fill&Go by services/fillandgo) — a car that didn't refuel in that
  // window has no entry.
  getFuel = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/car-fuel`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      const data = response.data || {};
      this.setState({fuelByCarId: data.cars || {}, fuelWindow: data.from ? {from: data.from, to: data.to} : null});
    } catch (e) {
      // Cars just show "—" for fuel, same as having no data.
    }
  };

  // Last 10 Fill&Go fuelings for one car.
  openFuelHistory = async (event, car) => {
    this.setState({fuelHistoryAnchor: event.currentTarget, fuelHistoryCar: car, fuelHistoryEntries: []});
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/cars/${car.id}/fuelings`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({fuelHistoryEntries: response.data || []});
    } catch (e) {
      // The Menu already shows "Nicio alimentare".
    }
  };

  closeFuelHistory = () => this.setState({fuelHistoryAnchor: null, fuelHistoryCar: null});

  getCars = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/cars`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({cars: response.data});
  };

  // Real total km per car, as synced once daily from ARobs's own Carriers
  // report (see the backend's services/trackgps.SyncYesterdayKm) — a car
  // with no synced days yet just has no entry here.
  getKm = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/car-km`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({kmByCarId: response.data || {}});
  };

  // Each car's single most recent service record — NextServiceKm from it is
  // what serviceStatusFor compares real km against.
  getLatestServices = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/car-services/latest`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({latestServiceByCarId: response.data || {}});
    } catch (e) {
      // No service history yet (or endpoint not deployed) — cars just show
      // with no due/soon flag, same as having no data.
    }
  };

  // 'due' (red) once real km has reached/passed the latest service's
  // next_service_km, 'soon' (yellow) once within SERVICE_DUE_SOON_KM of it,
  // 'ok' (neutral) for any other tracked car, null only when there isn't
  // enough data (no service history, no next km set, or real km not synced
  // yet) to say anything at all — same reasoning as docStatus: a car whose
  // service is simply fine still needs to show that, not look unmonitored.
  serviceStatusFor = (car) => {
    const service = this.state.latestServiceByCarId[car.id];
    const currentKm = this.state.kmByCarId[car.id];
    if (!service || !service.next_service_km || currentKm == null) {
      return null;
    }
    const remaining = service.next_service_km - currentKm;
    if (remaining <= 0) {
      return 'due';
    }
    if (remaining <= SERVICE_DUE_SOON_KM) {
      return 'soon';
    }
    return 'ok';
  };

  // The "mai sunt X km" figure for the 'ok'/'soon' badge — null alongside
  // serviceStatusFor's null.
  serviceRemainingKmFor = (car) => {
    const service = this.state.latestServiceByCarId[car.id];
    const currentKm = this.state.kmByCarId[car.id];
    if (!service || !service.next_service_km || currentKm == null) {
      return null;
    }
    return service.next_service_km - currentKm;
  };

  openCreateModal = () => this.setState({modalOpen: true, editingCar: null});

  openEditModal = (car) => this.setState({modalOpen: true, editingCar: car});

  closeModal = () => this.setState({modalOpen: false});

  handleSaved = async () => {
    this.setState({modalOpen: false});
    await this.getCars();
  };

  deleteCar = async (car) => {
    if (!window.confirm(`Ștergi mașina ${car.make} ${car.model} (${car.plate_number})?`)) {
      return;
    }
    const token = localStorage.getItem('access_token');
    await axios.default.delete(`${baseUrl}v1/admin/cars/${car.id}`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    await this.getCars();
  };

  openServiceModal = (car) => this.setState({serviceModalOpen: true, serviceModalCar: car});

  closeServiceModal = () => this.setState({serviceModalOpen: false});

  // Last 5 synced days of real km for one car — see the backend's
  // ListCarKmHistory (services/trackgps.SyncYesterdayKm is what actually
  // fills car_daily_km, once a day).
  openKmHistory = async (event, car) => {
    this.setState({kmHistoryAnchor: event.currentTarget, kmHistoryCar: car, kmHistoryEntries: []});
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/cars/${car.id}/km-history`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({kmHistoryEntries: response.data || []});
    } catch (e) {
      // Leave kmHistoryEntries empty — the Menu already shows "Nicio zi
      // sincronizată încă" for that case, same as a genuinely empty history.
    }
  };

  closeKmHistory = () => this.setState({kmHistoryAnchor: null, kmHistoryCar: null});

  openKmEdit = (car) => {
    const currentKm = this.state.kmByCarId[car.id];
    this.setState({
      kmEditCar: car,
      kmEditValue: currentKm != null ? String(Math.round(currentKm)) : '',
      kmEditError: '',
    });
  };

  closeKmEdit = () => this.setState({kmEditCar: null});

  // Saves the admin-entered total as a manual correction — the backend
  // stores it as the difference from the car's current total (see
  // AddManualKmAdjustment) rather than overwriting car_daily_km history, so
  // ARobs's own synced numbers are never touched.
  saveKmEdit = async () => {
    const {kmEditCar, kmEditValue} = this.state;
    if (kmEditValue === '' || isNaN(Number(kmEditValue)) || Number(kmEditValue) < 0) {
      this.setState({kmEditError: 'Km invalid.'});
      return;
    }
    this.setState({kmEditSaving: true, kmEditError: ''});
    try {
      const token = localStorage.getItem('access_token');
      await axios.default.put(`${baseUrl}v1/admin/cars/${kmEditCar.id}/km`, {km: Number(kmEditValue)}, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({kmEditCar: null});
      await this.getKm();
    } catch (e) {
      this.setState({kmEditError: 'Eroare la salvare.'});
    } finally {
      this.setState({kmEditSaving: false});
    }
  };

  recoverCar = async (car) => {
    const token = localStorage.getItem('access_token');
    await axios.default.put(`${baseUrl}v1/admin/cars/${car.id}/recover`, {}, {
      headers: {Authorization: `Bearer ${token}`},
    });
    await this.getCars();
  };

  render() {
    const cars = this.state.cars || [];
    const {modalOpen, editingCar, showDeleted, kmByCarId, fuelByCarId, fuelWindow} = this.state;
    const fuelWindowTitle = fuelWindow
      ? `${formatDate(parseDateOnly(fuelWindow.from))} – ${formatDate(parseDateOnly(fuelWindow.to))}`
      : '';
    const visibleCars = showDeleted ? cars : cars.filter((c) => !c.deleted);
    const rowClass = (car) => {
      if (car.deleted) {
        return 'deliverers-row-deleted';
      }
      const status = this.serviceStatusFor(car);
      if (status === 'due') {
        return 'cars-row-service-due';
      }
      if (status === 'soon') {
        return 'cars-row-service-soon';
      }
      return '';
    };

    return (
      <div className="deliverers-page deliverers-page-scroll">
        <div className="deliverers-section">
          <div className="deliverers-section-header">
            <h3 className="deliverers-section-title">Mașini</h3>
            <div className="deliverers-header-actions">
              <FormControlLabel
                control={
                  <Switch
                    checked={showDeleted}
                    onChange={(event) => this.setState({showDeleted: event.target.checked})}
                  />
                }
                label="Arată mașinile șterse"
              />
              <Button variant="contained" color="primary" onClick={this.openCreateModal}>
                + Adaugă mașină
              </Button>
            </div>
          </div>
          <div className="deliverers-table-scroll">
          <table className="deliverers-table">
            <thead>
            <tr>
              <th>Marcă</th>
              <th>Model</th>
              <th>Număr de înmatriculare</th>
              <th>Pin Rompetrol</th>
              <th>Km real</th>
              <th title={fuelWindowTitle}>Carburant (30 zile)</th>
              <th title={`Litri la 100 km și lei pe km reali, ${fuelWindowTitle}`}>Consum</th>
              <th>Stare service</th>
              <th>Documente</th>
              <th>Acțiuni</th>
            </tr>
            </thead>
            <tbody>
            {visibleCars.map((car) => {
              const serviceStatus = car.deleted ? null : this.serviceStatusFor(car);
              const serviceRemainingKm = car.deleted ? null : this.serviceRemainingKmFor(car);
              return (
              <tr key={car.id} className={rowClass(car)}>
                <td>{car.make}</td>
                <td>{car.model}</td>
                <td>{car.plate_number}</td>
                <td>{car.rompetrol_pin || '—'}</td>
                <td>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    {kmByCarId[car.id] != null ? `${Math.round(kmByCarId[car.id]).toLocaleString('ro-RO')} km` : '—'}
                    <IconButton size="small" title="Istoric km" onClick={(event) => this.openKmHistory(event, car)}>
                      <HistoryIcon fontSize="small"/>
                    </IconButton>
                    <IconButton size="small" title="Corectează km" onClick={() => this.openKmEdit(car)}>
                      <EditIcon fontSize="small"/>
                    </IconButton>
                  </div>
                </td>
                <td>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    {fuelByCarId[car.id] ? (
                      <span title={`${fuelByCarId[car.id].fuelings} alimentări`}>
                        {formatNumber(fuelByCarId[car.id].liters, 0)} l · {formatNumber(fuelByCarId[car.id].total, 0)} lei
                      </span>
                    ) : '—'}
                    <IconButton size="small" title="Alimentări" onClick={(event) => this.openFuelHistory(event, car)}>
                      <LocalGasStationIcon fontSize="small"/>
                    </IconButton>
                  </div>
                </td>
                <td>
                  {fuelByCarId[car.id] && fuelByCarId[car.id].km > 0 ? (
                    <span title={`${Math.round(fuelByCarId[car.id].km).toLocaleString('ro-RO')} km reali`}>
                      {formatNumber(fuelByCarId[car.id].liters_per_100km, 1)} l/100 km · {formatNumber(fuelByCarId[car.id].lei_per_km, 2)} lei/km
                    </span>
                  ) : '—'}
                </td>
                <td>
                  {serviceStatus === 'due' && (
                    <span className="cars-service-badge cars-service-badge-due">Service depășit</span>
                  )}
                  {serviceStatus === 'soon' && (
                    <span className="cars-service-badge cars-service-badge-soon">
                      Service în curând ({Math.round(serviceRemainingKm).toLocaleString('ro-RO')} km)
                    </span>
                  )}
                  {serviceStatus === 'ok' && (
                    <span className="cars-service-badge cars-service-badge-ok">
                      OK ({Math.round(serviceRemainingKm).toLocaleString('ro-RO')} km)
                    </span>
                  )}
                  {!serviceStatus && '—'}
                </td>
                <td>
                  <div className="cars-doc-badges">
                    {DOC_FIELDS.map(({key, label}) => {
                      const status = docStatus(car[key]);
                      if (!status) {
                        return null;
                      }
                      const date = parseDateOnly(car[key]);
                      const badgeClass = status === 'expired'
                        ? 'cars-service-badge-due'
                        : status === 'warning'
                          ? 'cars-service-badge-soon'
                          : 'cars-service-badge-ok';
                      return (
                        <span
                          key={key}
                          className={`cars-service-badge ${badgeClass}`}
                          title={`${label}: ${date.toLocaleDateString('ro-RO')}`}
                        >
                          {label} {date.toLocaleDateString('ro-RO')}
                        </span>
                      );
                    })}
                    {DOC_FIELDS.every(({key}) => !docStatus(car[key])) && !car.rca_file_url && !car.casco_file_url && '—'}
                    {car.rca_file_url && (
                      <a href={car.rca_file_url} target="_blank" rel="noreferrer" title="Descarcă RCA">
                        <IconButton size="small">
                          <GetAppIcon fontSize="small"/>
                        </IconButton>
                      </a>
                    )}
                    {car.casco_file_url && (
                      <a href={car.casco_file_url} target="_blank" rel="noreferrer" title="Descarcă CASCO">
                        <IconButton size="small">
                          <GetAppIcon fontSize="small"/>
                        </IconButton>
                      </a>
                    )}
                  </div>
                </td>
                <td>
                  <div className="deliverers-row-actions">
                    <IconButton size="small" title="Editează" onClick={() => this.openEditModal(car)}>
                      <EditIcon fontSize="small"/>
                    </IconButton>
                    <IconButton size="small" title="Service" onClick={() => this.openServiceModal(car)}>
                      <BuildIcon fontSize="small"/>
                    </IconButton>
                    {car.deleted ? (
                      <IconButton size="small" title="Recuperează" onClick={() => this.recoverCar(car)}>
                        <RestoreFromTrashIcon fontSize="small"/>
                      </IconButton>
                    ) : (
                      <IconButton size="small" title="Șterge" onClick={() => this.deleteCar(car)}>
                        <DeleteIcon fontSize="small"/>
                      </IconButton>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            </tbody>
          </table>
          </div>
        </div>

        <CarModal
          key={`${editingCar ? editingCar.id : 'new'}-${modalOpen}`}
          open={modalOpen}
          car={editingCar}
          onClose={this.closeModal}
          onSaved={this.handleSaved}
          onFileUploaded={this.getCars}
        />

        <CarServiceModal
          key={`${this.state.serviceModalCar ? this.state.serviceModalCar.id : 'none'}-${this.state.serviceModalOpen}`}
          open={this.state.serviceModalOpen}
          car={this.state.serviceModalCar}
          onClose={this.closeServiceModal}
          onChanged={this.getLatestServices}
        />

        <Menu anchorEl={this.state.kmHistoryAnchor} open={!!this.state.kmHistoryAnchor} onClose={this.closeKmHistory}>
          {this.state.kmHistoryCar && (
            <div className="deliverers-status-menu-header">
              Istoric km — {this.state.kmHistoryCar.plate_number}
            </div>
          )}
          {this.state.kmHistoryEntries.length === 0 ? (
            <MenuItem disabled>Nicio zi sincronizată încă</MenuItem>
          ) : this.state.kmHistoryEntries.map((entry) => (
            <MenuItem key={entry.id} disabled>
              {formatDate(parseDateOnly(entry.date))} — {Math.round(entry.km).toLocaleString('ro-RO')} km
              {entry.is_manual && ' (corecție manuală)'}
            </MenuItem>
          ))}
        </Menu>

        <Menu anchorEl={this.state.fuelHistoryAnchor} open={!!this.state.fuelHistoryAnchor} onClose={this.closeFuelHistory}>
          {this.state.fuelHistoryCar && (
            <div className="deliverers-status-menu-header">
              Alimentări — {this.state.fuelHistoryCar.plate_number}
            </div>
          )}
          {this.state.fuelHistoryEntries.length === 0 ? (
            <MenuItem disabled>Nicio alimentare</MenuItem>
          ) : this.state.fuelHistoryEntries.map((entry) => (
            <MenuItem key={entry.id} disabled>
              {new Date(entry.fueled_at).toLocaleString('ro-RO', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})}
              {' — '}{formatNumber(entry.liters, 2)} l · {formatNumber(entry.total, 2)} lei · {entry.station}
            </MenuItem>
          ))}
        </Menu>

        <Dialog open={!!this.state.kmEditCar} onClose={this.closeKmEdit} fullWidth maxWidth="xs">
          <DialogTitle>
            Corectează km — {this.state.kmEditCar && this.state.kmEditCar.plate_number}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Km real"
              type="number"
              fullWidth
              margin="normal"
              autoFocus
              value={this.state.kmEditValue}
              onChange={(event) => this.setState({kmEditValue: event.target.value})}
            />
            {this.state.kmEditError && <div className="deliverer-modal-error">{this.state.kmEditError}</div>}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeKmEdit} disabled={this.state.kmEditSaving}>Anulează</Button>
            <Button onClick={this.saveKmEdit} disabled={this.state.kmEditSaving} variant="contained" color="primary">
              Salvează
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}
